import { api } from './client'

/**
 * Per-firm column aliases (spec 061, backend CC-50234).
 *
 * `reportKey` is the report id for the ~119 generic reports, or the page's
 * `actionKey` for the 21 specialized ones — one text key covers both, because
 * specialized pages have no `cb_Reports` column set to key off.
 *
 * READ is open to every authenticated user: a firm vocabulary only helps if
 * everyone sees the same words. WRITE is admin-gated server-side; `canManage`
 * on the read tells the UI whether to offer the editor without a second call.
 */
const columnAliasService = {
  /**
   * → `{ aliases, meta, canManage, permissionResolved, storageConfigured }`
   *
   * Never throws: an alias read must not be able to break a report. On any
   * failure it resolves to the empty, refusing shape — CB's own labels, no
   * editor — because a report showing CB's labels is correct, just not
   * customised. `storageConfigured:false` distinguishes "this environment has
   * no D1 binding" from "this firm has renamed nothing", which are otherwise
   * the same empty map (the CC-49595 trap).
   */
  async get(reportKey) {
    try {
      const data = await api.get(`/report-column-aliases/${encodeURIComponent(reportKey)}`)
      const d = data?.data || {}
      return {
        aliases: d.aliases || {},
        meta: d.meta || {},
        canManage: !!d.canManage,
        permissionResolved: !!d.permissionResolved,
        storageConfigured: d.storageConfigured !== false,
      }
    } catch {
      return { aliases: {}, meta: {}, canManage: false, permissionResolved: false, storageConfigured: false }
    }
  },

  /** Set one column's alias. A blank alias resets that column to CB's label. */
  async set(reportKey, columnId, alias) {
    const data = await api.put(`/report-column-aliases/${encodeURIComponent(reportKey)}`, {
      columnId: String(columnId),
      alias,
    })
    return data?.data || {}
  },

  /** Reset every column on this report back to CB's labels. */
  async resetAll(reportKey) {
    const data = await api.delete(`/report-column-aliases/${encodeURIComponent(reportKey)}`)
    return data?.data || {}
  },

  /** Who renamed what, when. Admin-gated — it names individuals. */
  async audit(reportKey) {
    const data = await api.get(`/report-column-aliases/${encodeURIComponent(reportKey)}/audit`)
    return data?.data?.entries || []
  },
}

export default columnAliasService
